import { Col, Row } from "react-bootstrap";
import { ESolarServiceProvider } from "../../../shared/oversight-core/enums/solar-service-provider";
import { IGoodWeSolarPlantView } from "../../../shared/oversight-core/interfaces/good-we-solar-plant-view";
import { IGroWattSolarPlantView } from "../../../shared/oversight-core/interfaces/gro-watt-solar-plant-view";
import { IPowerHubSolarPlantView } from "../../../shared/oversight-core/interfaces/powerhub-solar-plant-view";
import { IScoutSolarPlantView } from "../../../shared/oversight-core/interfaces/scout-solar-plant-view";
import { IVrmSolarPlantView } from "../../../shared/oversight-core/interfaces/vrm-solar-plant-view";
import ActiveInactiveIndicator from "../../../shared/oversight-core/ui-elements/active-inactive-indicator/active-inactive-indicator";
import AppDropDown from "../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppTabs, {
  TabComponentInterface,
} from "../../../shared/oversight-core/ui-elements/app-tabs/app-tabs";
import ArrowButton from "../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { getServiceProviderLogo } from "../../../shared/oversight-core/utils/get-service-provider-logo";
import { useCanOperate, useIsGuest } from "../../../shared/utils/org-roles";
import GoodWeSiteDashboard from "./site-dashboard/good-we-site-dashboard";
import GroWattSiteDashboard from "./site-dashboard/gro-watt-site-dashboard";
import PowerHubSiteDashboard from "./site-dashboard/powerhub-site-dashboard";
import ScoutSiteDashboard from "./site-dashboard/scout-site-dashboard";
import VrmSiteDashboard from "./site-dashboard/vrm-site-dashboard";
import SiteDevices from "./site-devices/site-devices";
import SiteErrorLog from "./site-error-log/site-error-log";
import {
  getEnableDisableLabel,
  ISiteViewModel,
  SitePlants,
} from "./view-site-helpers";

const SITE_TAB_BUTTONS: TabComponentInterface[] = [
  {
    buttonName: "Dashboard",
    selectedColor: "blueColor",
    id: 1,
  },
  {
    buttonName: "Devices",
    selectedColor: "blueColor",
    id: 2,
  },
  {
    buttonName: "Error Log",
    selectedColor: "blueColor",
    id: 3,
  },
];

interface ViewSiteContentProps {
  plantId: string;
  serviceProvider: ESolarServiceProvider;
  site: ISiteViewModel;
  plants: SitePlants;
  isViewingSolarPlant: boolean;
  activeTab: number;
  onTabChange: (id: number) => void;
  onBack: () => void;
  onDisable: () => void;
  onEdit: () => void;
  onChangeLog: () => void;
  onExport: () => void;
}

const LastUpdateLabel = ({ site }: { site: ISiteViewModel }) => {
  if (!site.showLastUpdate) {
    return null;
  }

  return (
    <Col className="text-light font-weight-400 font-size-12">
      Last Update <span className="text-dark">{site.lastUpdate}</span>
    </Col>
  );
};

const PowerHubDashboard = ({
  plantId,
  solarPlant,
}: {
  plantId: string;
  solarPlant?: IPowerHubSolarPlantView;
}) => {
  if (!solarPlant) {
    return <SpinnerModal show={true} />;
  }

  return <PowerHubSiteDashboard plantId={plantId} solarPlant={solarPlant} />;
};

const ScoutDashboard = ({
  solarPlant,
}: {
  solarPlant?: IScoutSolarPlantView;
}) => {
  if (!solarPlant) {
    return <SpinnerModal show={true} />;
  }

  return <ScoutSiteDashboard solarPlant={solarPlant} />;
};

const SiteDashboard = ({
  plantId,
  serviceProvider,
  plants,
  isViewingSolarPlant,
}: {
  plantId: string;
  serviceProvider: ESolarServiceProvider;
  plants: SitePlants;
  isViewingSolarPlant: boolean;
}) => {
  if (serviceProvider === ESolarServiceProvider.VRM) {
    return (
      <VrmSiteDashboard
        plantId={plantId}
        solarPlant={plants.vrm as IVrmSolarPlantView}
        isViewingSolarPlant={isViewingSolarPlant}
      />
    );
  }

  if (serviceProvider === ESolarServiceProvider.GROWATT) {
    return (
      <GroWattSiteDashboard
        plantId={plantId}
        solarPlant={plants.growatt as IGroWattSolarPlantView}
      />
    );
  }

  if (serviceProvider === ESolarServiceProvider.POWERHUB) {
    return <PowerHubDashboard plantId={plantId} solarPlant={plants.powerHub} />;
  }

  if (serviceProvider === ESolarServiceProvider.SCOUT) {
    return <ScoutDashboard solarPlant={plants.scout} />;
  }

  return (
    <GoodWeSiteDashboard
      plantId={plantId}
      solarPlant={plants.goodWe as IGoodWeSolarPlantView}
    />
  );
};

const SiteActiveTab = ({
  activeTab,
  plantId,
  serviceProvider,
  site,
  plants,
  isViewingSolarPlant,
}: {
  activeTab: number;
  plantId: string;
  serviceProvider: ESolarServiceProvider;
  site: ISiteViewModel;
  plants: SitePlants;
  isViewingSolarPlant: boolean;
}) => {
  if (!site.showTabs) {
    return (
      <SiteDashboard
        plantId={plantId}
        serviceProvider={serviceProvider}
        plants={plants}
        isViewingSolarPlant={isViewingSolarPlant}
      />
    );
  }

  if (activeTab === 2) {
    return <SiteDevices plantId={plantId} serviceProvider={serviceProvider} />;
  }

  if (activeTab === 3) {
    return (
      <SiteErrorLog
        plantId={plantId}
        zoneId={site.errorLogZoneId}
        serviceProvider={serviceProvider}
      />
    );
  }

  return (
    <SiteDashboard
      plantId={plantId}
      serviceProvider={serviceProvider}
      plants={plants}
      isViewingSolarPlant={isViewingSolarPlant}
    />
  );
};

const SiteViewTabs = ({
  showTabs,
  activeTab,
  onTabChange,
}: {
  showTabs: boolean;
  activeTab: number;
  onTabChange: (id: number) => void;
}) => {
  if (!showTabs) {
    return null;
  }

  return (
    <Col>
      <AppTabs
        buttons={SITE_TAB_BUTTONS}
        onChange={onTabChange}
        selectedTabId={activeTab}
      />
    </Col>
  );
};

const ViewSiteContent = ({
  plantId,
  serviceProvider,
  plants,
  isViewingSolarPlant,
  activeTab,
  onTabChange,
  onBack,
  onDisable,
  onEdit,
  onChangeLog,
  onExport,
  site: siteView,
}: ViewSiteContentProps) => {
  const isGuest = useIsGuest();
  const canOperate = useCanOperate();
  // A monitoring guest sees the dashboard only: devices and the error log are refused to them.
  const site = isGuest ? { ...siteView, showTabs: false } : siteView;
  const exportItem = { text: "Export", onClick: onExport };
  const changeLogItem = { text: "Change Log", onClick: onChangeLog };
  // A guest's report is redacted by the backend, and the change log names people, so a guest
  // gets the report only. A viewer reads the change log but changes nothing about the site.
  const menuItems = isGuest
    ? [exportItem]
    : canOperate
      ? [
          {
            text: getEnableDisableLabel(site.isActive),
            onClick: onDisable,
          },
          {
            text: "Edit Site",
            onClick: onEdit,
          },
          changeLogItem,
          exportItem,
        ]
      : [changeLogItem, exportItem];

  return (
    <>
      <Row className="align-items-center justify-content-between">
        <Col className="col-12 col-lg-auto">
          <Row className="align-items-center ms-0">
            <Col className="col-auto">
              <ArrowButton onBack={onBack} />
            </Col>
            <Col>
              <Row className="align-items-center">
                <Col className="col-auto pe-0 text-dark font-weight-500 font-size-24">
                  {site.displayName}
                </Col>
                <Col>
                  <ActiveInactiveIndicator isActive={site.isStatusActive} />
                </Col>
              </Row>
              <Row className="row-cols-auto align-items-center">
                <Col className="pe-0">
                  <img
                    src={getServiceProviderLogo(serviceProvider)}
                    alt=""
                    width={site.logoSize}
                    height={site.logoSize}
                  />
                </Col>
                <LastUpdateLabel site={site} />
                <Col className="text-light font-weight-400 font-size-12">
                  Status <span className="text-dark">{site.statusLabel}</span>
                </Col>
                <Col className="text-light font-weight-400 font-size-12">
                  Local Time <span className="text-dark">{site.localTime}</span>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col
          className={
            site.showTabs
              ? "col-12 col-lg-6 col-xxl-4 mt-2 mt-xxl-0"
              : "col-auto mt-2 mt-xxl-0"
          }
        >
          <Row className="align-items-center justify-content-end">
            <Col className="col-auto">
              <AppDropDown items={menuItems} size="md" />
            </Col>
            <Col className="ps-0">
              <SiteViewTabs
                showTabs={site.showTabs}
                activeTab={activeTab}
                onTabChange={onTabChange}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <SiteActiveTab
            activeTab={activeTab}
            plantId={plantId}
            serviceProvider={serviceProvider}
            site={site}
            plants={plants}
            isViewingSolarPlant={isViewingSolarPlant}
          />
        </Col>
      </Row>
    </>
  );
};

export default ViewSiteContent;
